import React, { useState, useEffect, useContext, useRef } from "react";
import {
  makeStyles,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Paper,
  Chip,
  Card,
  CircularProgress,
  Popover,
  Tooltip,
} from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import SendIcon from "@material-ui/icons/Send";
import EmojiEmotionsIcon from "@material-ui/icons/EmojiEmotions";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DoneIcon from "@material-ui/icons/Done";
import GetAppIcon from "@material-ui/icons/GetApp";
import ReplyIcon from "@material-ui/icons/Reply";
import ClearIcon from "@material-ui/icons/Clear";
import { toast } from "react-toastify";
import { green } from "@material-ui/core/colors";

import api from "../../services/api";
import ModalImageCors from "../ModalImageCors";
import { getBackendUrl } from "../../config";
import { getSocket } from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
// feat_341 — fmtSmartTimestamp (feat_315) formatea en la tz de la EMPRESA y no
// tumba el hilo con un timestamp roto; date-fns `format` usaba la tz del navegador.
import {
  fmtSmartTimestamp,
  fmtDaySeparator,
  startOfDayInTz
} from "../../translate/localeHelper";
import whatsBackground from "../../assets/wa-background.webp";

// feat_341 — techo del guard de subida. nginx corta en 20 MB por REQUEST
// (client_max_body_size en el container del frontend) y devuelve HTML crudo,
// que toastError no sabe leer. 18 MB deja margen para el resto del multipart.
const MAX_UPLOAD_BYTES = 18 * 1024 * 1024;

const QUICK_EMOJIS = [
  "😀","😂","👍","❤️","🙏","👋","✅","🔥","😊","👏","🤔","😅","💯","🎉","⚡",
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.background.paper,
    flex: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    minHeight: 56,
  },

  headerInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 0,
  },

  userName: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  internalChip: {
    backgroundColor: "#1976d2",
    color: "#fff",
    height: 20,
    fontSize: 11,
  },

  messagesArea: {
    backgroundImage: theme.palette.type === "dark"
      ? `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 1l9 9-9 9-9-9z' fill='none' stroke='rgba(255,255,255,0.04)' stroke-width='1'/%3E%3C/svg%3E")`
      : `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#0d1117" : "transparent",
    backgroundRepeat: "repeat",
    backgroundSize: theme.palette.type === "dark" ? "20px 20px" : "auto",
    flex: 1,
    overflowY: "auto",
    padding: "20px 20px",
    display: "flex",
    flexDirection: "column",
    ...theme.scrollbarStyles,
  },

  msgRowRight: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 6,
  },

  msgRowLeft: {
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: 6,
  },

  msgBubbleMine: {
    background: theme.palette.type === "dark" ? "#005c4b" : "#dcf8c6",
    color: theme.palette.text.primary,
    borderRadius: "8px 0 8px 8px",
    padding: "6px 10px 4px",
    maxWidth: "70%",
    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    wordBreak: "break-word",
  },

  msgBubbleOther: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: "0 8px 8px 8px",
    padding: "6px 10px 4px",
    maxWidth: "70%",
    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    wordBreak: "break-word",
  },

  msgText: {
    fontSize: 14,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },

  msgTime: {
    fontSize: 11,
    color: "#888",
    textAlign: "right",
    marginTop: 2,
  },

  // feat_341 — píldora centrada del separador de día (misma forma que
  // MessagesList.dailyTimestamp, pero de ancho automático: acá el texto va de
  // "Hoy" a "20/08/2025" y un width fijo de 110px lo cortaría).
  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    backgroundColor: theme.palette.background.paper,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
    flex: "none",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: "6px 12px",
    alignSelf: "center",
    textTransform: "capitalize",
  },

  loadingWrapper: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },

  inputArea: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: 8,
    flex: "none",
  },

  // feat_342 — bloque de cita dentro del globo. Portado de
  // MessagesList/index.js:131-207 y SIMPLIFICADO: sin thumbnail, sin colores por
  // participante y sin los márgenes negativos (-80px) que allá compensan el ancho
  // fijo de la burbuja del chat principal; acá la burbuja es fluida.
  quotedContainer: {
    display: "flex",
    overflow: "hidden",
    borderRadius: 6,
    marginBottom: 4,
    cursor: "pointer",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.10)"
        : "rgba(0, 0, 0, 0.06)",
  },

  quotedSideColor: {
    flex: "none",
    width: 4,
    backgroundColor: "#6bcbef",
  },

  quotedSideColorMine: {
    backgroundColor: "#35cd96",
  },

  quotedBody: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    padding: "5px 8px",
  },

  quotedAuthor: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#6bcbef",
  },

  quotedText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // feat_342 — flash de ~1 s al saltar al mensaje original citado. Sin esto el
  // scroll deja la vista en el medio del hilo y no se sabe CUÁL es el mensaje.
  bubbleHighlight: {
    animation: "$internalChatFlash 1.1s ease",
  },

  "@keyframes internalChatFlash": {
    "0%": { boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.70)" },
    "70%": { boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.45)" },
    "100%": { boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)" },
  },

  // feat_342 — barra "respondiendo a…" arriba del input (patrón
  // MessageInput.renderReplyingMessage:936-963).
  replyingBar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flex: "none",
    padding: theme.spacing(0.5, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },

  replyingPreview: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    overflow: "hidden",
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.05)",
  },
}));

/**
 * InternalChat — full right-panel chat for internal (user-to-user) messages.
 *
 * Props:
 *   user    — the partner user object { id, name, profilePicUrl, email }
 *   onBack  — called when the back button is clicked
 */
const InternalChat = ({ user: partnerUser, onBack }) => {
  const classes = useStyles();
  const { user: currentUser } = useContext(AuthContext);
  // feat_341 — tz de la empresa para hora y separadores. `businessTimezone` está en
  // publicKeys (feat_315) → la leen todos los perfiles. Sin la key queda undefined
  // y los helpers caen a la tz del navegador.
  const { getSetting } = useContext(SettingsContext);
  const timezone = getSetting("businessTimezone") || undefined;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  // feat_342 — la cita en curso es estado LOCAL a propósito. NO se usa
  // ReplyingMessageContext: ese provider es un slot ÚNICO y GLOBAL (un solo
  // useState(null) sin scope por ticket) → con un ticket abierto al mismo tiempo,
  // el chat interno y el chat del ticket se pisarían la cita entre sí.
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  // feat_342 — ref al contenedor scrollable (antes solo existía el <div> final).
  // Hace falta para leer scrollTop/scrollHeight: sin eso no se puede saber si el
  // usuario está cerca del fondo ni bajar sin animación.
  const messagesAreaRef = useRef(null);
  // feat_342 — anchors por msg.id para el salto al mensaje original citado.
  const msgRefs = useRef(new Map());
  const nearBottomRef = useRef(true);
  // feat_342 — "bajá al fondo sí o sí": apertura de conversación y envío propio.
  const forceBottomRef = useRef(true);

  const isNearBottom = () => {
    const area = messagesAreaRef.current;
    if (!area) return true;
    return area.scrollHeight - area.scrollTop - area.clientHeight < 120;
  };

  const handleMessagesScroll = () => {
    nearBottomRef.current = isNearBottom();
  };

  // feat_342 — FIX DE SCROLL. El efecto anterior dependía SOLO de [messages] y el
  // contenedor vive detrás de `loading ? <spinner> : <lista>`. React es 16.13 (sin
  // auto-batching en promesas): `setMessages` (.then) y `setLoading(false)`
  // (.finally) son DOS renders distintos. En el primero la lista todavía no está
  // montada → `messagesEndRef.current` es null y el scroll no ocurre; en el segundo
  // el efecto NO vuelve a correr porque `messages` no cambió. Resultado: la
  // conversación abría arriba de todo y había que scrollear a mano.
  // Agregar `loading` a las deps cierra ese hueco.
  useEffect(() => {
    if (loading) return;
    const area = messagesAreaRef.current;
    if (!area) return;

    if (forceBottomRef.current) {
      // Apertura de la conversación o envío propio → al fondo, sin animación.
      area.scrollTop = area.scrollHeight;
      forceBottomRef.current = false;
      nearBottomRef.current = true;
      // Las imágenes se miden DESPUÉS de pintar y empujan el alto hacia abajo; un
      // segundo ajuste diferido evita quedar a medio camino en un hilo con fotos.
      const timer = setTimeout(() => {
        const a = messagesAreaRef.current;
        if (a && nearBottomRef.current) a.scrollTop = a.scrollHeight;
      }, 300);
      return () => clearTimeout(timer);
    }

    // Mensaje nuevo por socket: seguir SOLO si el usuario ya estaba abajo. Si está
    // leyendo mensajes viejos no se le mueve la vista (patrón estándar).
    if (nearBottomRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    return undefined;
  }, [messages, loading]);

  // Focus text input when partner changes; restore draft
  useEffect(() => {
    if (!partnerUser?.id) return;
    const savedDraft = localStorage.getItem(`internalChat_draft_${partnerUser.id}`);
    if (savedDraft) setNewMsg(savedDraft);
    setTimeout(() => textInputRef.current?.focus(), 100);
    // NOTE: cleanup intentionally does NOT remove localStorage draft —
    // draft is only cleared after a successful send.
  }, [partnerUser?.id]);

  // Fetch conversation messages
  useEffect(() => {
    if (!partnerUser?.id) return;
    setLoading(true);
    // feat_342 — cada conversación abre en su ÚLTIMO mensaje.
    forceBottomRef.current = true;
    nearBottomRef.current = true;
    // feat_342 — la cita es por conversación: cambiar de contraparte la descarta.
    setReplyingTo(null);
    setHighlightedMsgId(null);
    api
      .get("/internal-messages", { params: { withUserId: partnerUser.id } })
      .then(({ data }) => {
        setMessages(data.messages || []);
        // feat_292: bulk-mark the whole history from this partner as read on open
        // (complements the per-message mark on live receive). Emits
        // internalMessageRead → the panel (on remount) shows badge 0.
        api
          .put("/internal-messages/read-all", { fromUserId: partnerUser.id })
          .catch(() => {});
      })
      .catch((err) => toastError(err))
      .finally(() => setLoading(false));
  }, [partnerUser?.id]);

  // Socket — listen for new messages in this conversation
  useEffect(() => {
    if (!partnerUser?.id) return;
    const socket = getSocket();

    // TicketsManager (etapa 1) también escucha "internalMessage" sobre este mismo
    // singleton: el off() va POR REFERENCIA o lo dejaría sordo.
    const handleInternalMessage = (data) => {
      const msg = data.message || data;
      const partnerId = partnerUser.id;
      const myId = currentUser?.id;
      if (
        (msg.fromUserId === partnerId && msg.toUserId === myId) ||
        (msg.fromUserId === myId && msg.toUserId === partnerId)
      ) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.find((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        // Mark as read if received
        if (msg.toUserId === myId && !msg.readAt) {
          api.put(`/internal-messages/${msg.id}/read`).catch(() => {});
        }
      }
    };
    socket.on("internalMessage", handleInternalMessage);

    return () => {
      // SINGLETON getSocket(): off() por referencia, NUNCA disconnect().
      socket.off("internalMessage", handleInternalMessage);
    };
  }, [partnerUser?.id, currentUser?.id]);

  const handleSend = async () => {
    if ((!newMsg.trim()) || !partnerUser?.id || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/internal-messages", {
        toUserId: partnerUser.id,
        message: newMsg.trim(),
        // feat_342 — solo el id; el backend valida scope y descarta la cita si no
        // pertenece a esta conversación. No se manda el objeto entero (el chat
        // principal sí lo hace, pero solo porque Baileys necesita ese payload).
        replyToId: replyingTo?.id,
      });
      // feat_341 — el endpoint devuelve un ARRAY de filas creadas (antes un objeto).
      // El fallback a [data] cubre un backend viejo durante una ventana de deploy.
      const created = Array.isArray(data) ? data : [data];
      // feat_342 — mensaje propio: siempre al fondo.
      forceBottomRef.current = true;
      // Add the sent message immediately (sender doesn't receive their own socket event)
      setMessages((prev) => {
        const nuevos = created.filter((m) => m && !prev.find((p) => p.id === m.id));
        return nuevos.length ? [...prev, ...nuevos] : prev;
      });
      setNewMsg("");
      setReplyingTo(null);
      localStorage.removeItem(`internalChat_draft_${partnerUser.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const handleCopy = (msgId, text) => {
    navigator.clipboard.writeText(text || "");
    setCopiedMsgId(msgId);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  // feat_341 — varios adjuntos en UNA sola request (`medias`, upload.array en el
  // backend). Cada archivo vuelve como su propia fila → su propio globo, con hora,
  // copy, zoom y descarga propios: el render de abajo no cambia.
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !partnerUser?.id) {
      e.target.value = "";
      return;
    }

    // Guard de tamaño ANTES de subir: nginx corta en 20 MB por request y su 413
    // llega como HTML crudo (no como el JSON de AppError) → toastError mostraría
    // un error inútil. Mejor frenarlo acá con un mensaje claro.
    const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
    if (totalBytes > MAX_UPLOAD_BYTES) {
      toast.error(i18n.t("internalChat.mediaTooLarge"));
      e.target.value = "";
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("medias", f));
      formData.append("toUserId", String(partnerUser.id));
      formData.append("message", files[0].name);
      // feat_342 — con varios adjuntos la cita queda en la PRIMERA fila (así lo
      // resuelve el backend). En multipart llega como string → Number() allá.
      if (replyingTo?.id) formData.append("replyToId", String(replyingTo.id));
      const { data } = await api.post("/internal-messages", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const created = Array.isArray(data) ? data : [data];
      forceBottomRef.current = true;
      setMessages((prev) => {
        const nuevos = created.filter((m) => m && !prev.find((p) => p.id === m.id));
        return nuevos.length ? [...prev, ...nuevos] : prev;
      });
      setReplyingTo(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
      e.target.value = "";
    }
  };

  // feat_342 — preview del mensaje citado. Sin adjunto → el texto tal cual.
  // Con adjunto → ícono + label i18n reusando `quotedMessage.*` (ya en los 4
  // idiomas). ⚠ Acá `mediaType` es el MIME CRUDO de multer ("image/png"), no el
  // "image"/"document" del chat principal → se clasifica por PREFIJO.
  const getQuotedPreview = (m) => {
    if (!m) return "";
    const mt = m.mediaType || "";
    if (!mt) return m.message || "";
    if (mt.startsWith("image/")) return `📷 ${i18n.t("quotedMessage.image")}`;
    if (mt.startsWith("video/")) return `🎬 ${i18n.t("quotedMessage.video")}`;
    if (mt.startsWith("audio/")) return `🎤 ${i18n.t("quotedMessage.audio")}`;
    if (mt === "application/pdf" || mt.startsWith("text/"))
      return `📄 ${i18n.t("quotedMessage.document")}`;
    return `📎 ${i18n.t("quotedMessage.attachment")}`;
  };

  // feat_342 — salto al mensaje original (el chat principal NO lo hace).
  // Si el original quedó fuera del tramo cargado (el index trae `limit: 200`) no
  // hay anchor a dónde ir → aviso suave, sin romper el hilo.
  const handleJumpToMessage = (targetId) => {
    const el = msgRefs.current.get(targetId);
    if (!el) {
      toast.info(i18n.t("internalChat.quotedNotLoaded"));
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMsgId(targetId);
    setTimeout(
      () => setHighlightedMsgId((cur) => (cur === targetId ? null : cur)),
      1200
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!partnerUser) return null;

  return (
    <div className={classes.root}>
      {/* Header */}
      <Card square elevation={0} className={classes.header}>
        <IconButton color="primary" onClick={onBack} size="small">
          <ArrowBackIosIcon />
        </IconButton>
        <div className={classes.headerInfo}>
          <Avatar
            src={partnerUser.profilePicUrl}
            style={{ width: 36, height: 36 }}
          >
            {partnerUser.name?.[0]}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Typography variant="subtitle1" className={classes.userName}>
              {partnerUser.name}
            </Typography>
            {partnerUser.email && (
              <Typography variant="caption" color="textSecondary" noWrap>
                {partnerUser.email}
              </Typography>
            )}
          </div>
          <Chip label="Chat Interno" size="small" className={classes.internalChip} />
        </div>
      </Card>

      {/* Messages area */}
      {loading ? (
        <div className={classes.loadingWrapper}>
          <CircularProgress style={{ color: green[500] }} />
        </div>
      ) : (
        <div
          className={classes.messagesArea}
          ref={messagesAreaRef}
          onScroll={handleMessagesScroll}
        >
          {messages.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ marginTop: 24 }}
            >
              No hay mensajes. ¡Escribe el primero!
            </Typography>
          )}

          {messages.map((msg, idx) => {
            const isMine = msg.fromUserId === currentUser?.id;
            // Vite (no CRA): getBackendUrl() lee window.ENV.VITE_BACKEND_URL (host del backend).
            // NUNCA usar process.env.REACT_APP_* acá — es undefined en runtime y caía a
            // window.location.origin (host del frontend) → media 404 (feat_290).
            const backendUrl = getBackendUrl().replace(/\/$/, "");
            const mediaFullUrl = msg.mediaUrl ? `${backendUrl}/public/${msg.mediaUrl}` : null;

            // feat_341 — separador de día. El PRIMER mensaje del hilo siempre lo lleva;
            // los demás, solo si cambió el día calendario respecto del anterior.
            // Se compara con startOfDayInTz en la tz de la EMPRESA (no isSameDay de
            // date-fns, que compara en la del navegador y desplazaría el corte).
            const prev = idx > 0 ? messages[idx - 1] : null;
            const showDaySeparator =
              !!msg.createdAt &&
              (!prev ||
                !prev.createdAt ||
                startOfDayInTz(msg.createdAt, timezone) !==
                  startOfDayInTz(prev.createdAt, timezone));
            const daySeparatorText = showDaySeparator
              ? fmtDaySeparator(msg.createdAt, { timezone })
              : "";

            return (
              <React.Fragment key={msg.id || idx}>
              {daySeparatorText && (
                <span className={classes.dailyTimestamp}>
                  <div className={classes.dailyTimestampText}>{daySeparatorText}</div>
                </span>
              )}
              <div
                className={isMine ? classes.msgRowRight : classes.msgRowLeft}
                onMouseEnter={() => setHoveredMsgId(msg.id)}
                onMouseLeave={() => setHoveredMsgId(null)}
                // feat_342 — anchor para el salto al mensaje original citado.
                ref={(el) => {
                  if (el) msgRefs.current.set(msg.id, el);
                  else msgRefs.current.delete(msg.id);
                }}
              >
                <Paper
                  elevation={0}
                  className={`${isMine ? classes.msgBubbleMine : classes.msgBubbleOther}${
                    highlightedMsgId === msg.id ? ` ${classes.bubbleHighlight}` : ""
                  }`}
                >
                  {/* feat_342 — bloque de la cita, arriba de todo el contenido del
                      globo. Click → scroll al original + flash. */}
                  {msg.replyTo && (
                    <div
                      className={classes.quotedContainer}
                      onClick={() => handleJumpToMessage(msg.replyTo.id)}
                      title={i18n.t("internalChat.jumpToQuoted")}
                    >
                      <span
                        className={`${classes.quotedSideColor}${
                          msg.replyTo.fromUserId === currentUser?.id
                            ? ` ${classes.quotedSideColorMine}`
                            : ""
                        }`}
                      />
                      <div className={classes.quotedBody}>
                        {msg.replyTo.fromUser?.name && (
                          <span className={classes.quotedAuthor}>
                            {msg.replyTo.fromUser.name}
                          </span>
                        )}
                        <div className={classes.quotedText}>
                          {getQuotedPreview(msg.replyTo)}
                        </div>
                      </div>
                    </div>
                  )}
                  {mediaFullUrl && msg.mediaType?.startsWith("image/") && (
                    // feat_291: reuso del visor del chat WhatsApp (zoom + descarga nativa),
                    // reemplaza el <img> plano sin descarga. mediaFullUrl es absoluta (getBackendUrl).
                    // feat_293: contenedor capa el thumbnail a ~220px (burbuja WhatsApp)
                    // + ícono de descarga propio SIEMPRE visible (garantía independiente
                    // del visor react-modal-image). Click en la imagen → fullscreen + zoom.
                    <div style={{ position: "relative", maxWidth: 220, marginBottom: 4 }}>
                      <ModalImageCors imageUrl={mediaFullUrl} compact />
                      <Tooltip title="Descargar" placement="top">
                        <a
                          href={mediaFullUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                          }}
                        >
                          <GetAppIcon style={{ fontSize: 18 }} />
                        </a>
                      </Tooltip>
                    </div>
                  )}
                  {mediaFullUrl && !msg.mediaType?.startsWith("image/") && (
                    <a href={mediaFullUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                      📎 {msg.message || "Archivo"}
                    </a>
                  )}
                  {(!mediaFullUrl || msg.mediaType?.startsWith("image/")) && (
                    <Typography className={classes.msgText}>
                      {msg.message || msg.body}
                    </Typography>
                  )}
                  {/* feat_341 — fmtSmartTimestamp (feat_315) en vez de
                      format(parseISO(...), "HH:mm"): formatea en la tz de la EMPRESA
                      (date-fns usaba la del navegador → +3 h con un cliente fuera de
                      ART), da "12:35" / "Ayer 09:15" / "lunes 18:00" / "20/08 12:35",
                      y tiene try/catch: un createdAt roto ya no tumba el hilo entero. */}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                                 fontSize: 11, float: "right", marginTop: 4,
                                 whiteSpace: "nowrap" }}>
                    {fmtSmartTimestamp(msg.createdAt, { timezone })}
                    {/* feat_342 — responder. Mismo patrón opacity/hoveredMsgId
                        que el copy de feat_293: cero estructura nueva. */}
                    <Tooltip title={i18n.t("messageOptionsMenu.reply")} placement="top">
                      <span
                        onClick={() => {
                          setReplyingTo(msg);
                          textInputRef.current?.focus();
                        }}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          opacity: hoveredMsgId === msg.id ? 1 : 0,
                          transition: "opacity 0.15s ease",
                          pointerEvents: hoveredMsgId === msg.id ? "auto" : "none",
                        }}
                      >
                        <ReplyIcon style={{ fontSize: 15, color: "inherit" }} />
                      </span>
                    </Tooltip>
                    <Tooltip title={copiedMsgId === msg.id ? "¡Copiado!" : "Copiar"} placement="top">
                      <span
                        onClick={() => handleCopy(msg.id, msg.message || msg.body)}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          opacity: hoveredMsgId === msg.id ? 1 : 0,
                          transition: "opacity 0.15s ease",
                          pointerEvents: hoveredMsgId === msg.id ? "auto" : "none",
                        }}
                      >
                        {copiedMsgId === msg.id
                          ? <DoneIcon style={{ fontSize: 14, color: "green" }} />
                          : <FileCopyIcon style={{ fontSize: 14, color: "inherit" }} />}
                      </span>
                    </Tooltip>
                  </span>
                </Paper>
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* feat_342 — barra "respondiendo a…", arriba del input */}
      {replyingTo && (
        <div className={classes.replyingBar}>
          <div className={classes.replyingPreview}>
            <span
              className={`${classes.quotedSideColor}${
                replyingTo.fromUserId === currentUser?.id
                  ? ` ${classes.quotedSideColorMine}`
                  : ""
              }`}
            />
            <div className={classes.quotedBody}>
              {replyingTo.fromUser?.name && (
                <span className={classes.quotedAuthor}>
                  {replyingTo.fromUser.name}
                </span>
              )}
              <div className={classes.quotedText}>
                {getQuotedPreview(replyingTo)}
              </div>
            </div>
          </div>
          <IconButton size="small" onClick={() => setReplyingTo(null)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* Input area */}
      <div className={classes.inputArea}>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <IconButton
          size="small"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={sending}
        >
          <AttachFileIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => setEmojiAnchor(e.currentTarget)}
        >
          <EmojiEmotionsIcon />
        </IconButton>

        <Popover
          open={Boolean(emojiAnchor)}
          anchorEl={emojiAnchor}
          onClose={() => setEmojiAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <div
            style={{
              padding: 8,
              display: "flex",
              flexWrap: "wrap",
              maxWidth: 200,
              gap: 4,
            }}
          >
            {QUICK_EMOJIS.map((emoji) => (
              <span
                key={emoji}
                style={{ fontSize: 20, cursor: "pointer", padding: 2 }}
                onClick={() => {
                  setNewMsg((prev) => prev + emoji);
                  setEmojiAnchor(null);
                }}
              >
                {emoji}
              </span>
            ))}
          </div>
        </Popover>

        <TextField
          value={newMsg}
          onChange={(e) => {
            setNewMsg(e.target.value);
            localStorage.setItem(`internalChat_draft_${partnerUser.id}`, e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Escribe un mensaje..."
          variant="outlined"
          size="small"
          fullWidth
          multiline
          maxRows={4}
          disabled={sending}
          inputRef={textInputRef}
        />

        <IconButton
          size="small"
          color="primary"
          onClick={handleSend}
          disabled={!newMsg.trim() || sending}
        >
          <SendIcon />
        </IconButton>
      </div>
    </div>
  );
};

export default InternalChat;
